import { CAPTURE_FULL, coordEq } from "@dailywars/core/engine";
import type { Coord } from "@dailywars/core/engine";

import {
  COORDS,
  captureRemainingAt,
  isIdle,
  isUnitSelected,
  ownUnitAt,
  tileOwnerAt,
} from "./tutorialStepHelpers";
import type { TutorialStep } from "./tutorialStepTypes";

const { sniperBarracks, neutralCity } = COORDS;

const FIRST_CAPTURE_REMAINING = CAPTURE_FULL / 2;

function allowsCaptureRiflemanMoveTile(
  store: Parameters<TutorialStep["allowsTile"]>[0],
  tile: Coord,
) {
  const mode = store.selection.mode;
  if (mode === "unit" || mode === "move-preview") return coordEq(tile, neutralCity);
  if (mode === "none") return coordEq(tile, sniperBarracks);
  return false;
}

function allowsCaptureRiflemanCaptureTile(
  store: Parameters<TutorialStep["allowsTile"]>[0],
  tile: Coord,
) {
  const mode = store.selection.mode;
  if (mode === "unit" || mode === "move-preview") return coordEq(tile, neutralCity);
  return coordEq(tile, neutralCity);
}

export const selectRiflemanForCapture: TutorialStep = {
  id: "select-rifleman-for-capture",
  buildKind: null,
  enableEndTurn: false,
  prompt: () => ({
    title: "Select your Rifleman",
    body: "Tap your northern Rifleman to select it.",
    highlight: { kind: "tile", coord: sniperBarracks },
  }),
  allowsTile: (_store, tile) => coordEq(tile, sniperBarracks),
  isComplete: (store) => isUnitSelected(store, { kind: "rifleman", coord: sniperBarracks }),
};

export const goToNeutralCity: TutorialStep = {
  id: "go-to-neutral-city",
  buildKind: null,
  enableEndTurn: false,
  prompt: (mode) => {
    if (mode === "move-preview") {
      return {
        title: "Start capture",
        body: "Tap the city to begin capturing. A full-health infantry unit can capture in two turns.",
        highlight: { kind: "tile", coord: neutralCity },
      };
    }
    return {
      title: "Head for the property",
      body: "Move your Rifleman onto the highlighted neutral city.",
      highlight: { kind: "tile", coord: neutralCity },
    };
  },
  allowsTile: allowsCaptureRiflemanMoveTile,
  isComplete: (store) =>
    ownUnitAt(store.localState, { coord: neutralCity, kind: "rifleman" }) &&
    captureRemainingAt(store.localState, neutralCity) === FIRST_CAPTURE_REMAINING &&
    isIdle(store),
};

export const finishCapture: TutorialStep = {
  id: "finish-capture",
  buildKind: null,
  enableEndTurn: false,
  prompt: () => ({
    title: "Finish the capture",
    body: "Your Rifleman held the city through the enemy turn. Select it and tap it again to finish capturing.",
    highlight: { kind: "tile", coord: neutralCity },
  }),
  allowsTile: allowsCaptureRiflemanCaptureTile,
  isComplete: (store) => tileOwnerAt(store.localState, neutralCity) === "p1" && isIdle(store),
};
